import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { logger } from '@/services/logger'
import { getCompanyById, patchCompany, type Address, type CompanyType } from '@/services/company'
import { COMPANY_ADDRESS_USAGE } from '@/constants/company'
import { useEmployeeStore } from '@/stores/employee'
import { useReferentialsStore, i18nService } from '@accor/customer-vue-services'
import { formatNumberDisplayByLang } from '@/helpers/formatters'
import { getDateInstance, getYear, isSameYearNow } from '@/helpers/date-helpers'
import { REWARD_THRESHOLD } from '@/constants/loyalty'
import { getCurrency } from '@/helpers/currency'

export const useCompanyStore = defineStore('company', () => {
  const company = ref<CompanyType>()
  const setCompanyPendingPromise = ref<Promise<void> | null>(null)

  const { referentials, refreshReferentials } = useReferentialsStore()

  const setCompany = async () => {
    if (setCompanyPendingPromise.value) {
      await setCompanyPendingPromise.value

      return
    }

    const promise = executeSetCompany()
    setCompanyPendingPromise.value = promise
    await promise
  }

  const executeSetCompany = async () => {
    const employeeStore = useEmployeeStore()

    try {
      if (!employeeStore.employee?.companyId) {
        await employeeStore.setEmployee()
      }

      if (!employeeStore.employee?.companyId) throw new Error('No company Id')

      const currency = getCurrency()

      const { data } = await getCompanyById(employeeStore.employee.companyId, currency)

      company.value = data
    } catch (error) {
      logger.error(error)

      throw error
    } finally {
      setCompanyPendingPromise.value = null
    }
  }

  const updateCompany = async (newCompanyData: Partial<CompanyType>) => {
    const employeeStore = useEmployeeStore()

    try {
      if (!employeeStore.employee?.companyId) {
        await employeeStore.setEmployee()
      }

      if (!employeeStore.employee?.companyId) throw new Error('No company Id')

      const { data } = await patchCompany(employeeStore.employee.companyId, newCompanyData)

      company.value = data
    } catch (error) {
      logger.error(error)

      throw error
    }
  }

  const postalAddress = computed(() => {
    return company.value?.addresses.find(
      (address) => address.usageType === COMPANY_ADDRESS_USAGE.POSTAL
    )
  })

  const formattedPostalAddress = async (address: Address) => {
    await refreshReferentials(i18nService.getCurrentLocale())

    const countryReferencial = referentials.countries.find(
      (country) => country.alpha2Code === address?.countryCode
    )

    const states = countryReferencial?.states as
      { code: string; codeGeo: string; name: string }[] | undefined

    const stateLabel =
      states && states.length > 0
        ? states.find((state) => state.code === address?.stateCode)?.name
        : null

    const formattedPostalAddress = {
      address1: address.address1,
      address2: address?.address2,
      zipCode: address?.zipCode,
      city: address?.city,
      country: countryReferencial?.name,
      state: stateLabel
    }

    return Object.values(formattedPostalAddress).filter(Boolean).join(', ')
  }

  const invitationLink = computed(() => {
    return company.value?.loyalty?.invitationLink
  })

  const balancePoints = computed(() => {
    return company.value?.loyalty?.balance?.points
  })

  const rewardPoints = computed(() => {
    if (!balancePoints.value) return undefined

    return parseInt(balancePoints.value, 10)
  })

  const hasReachedRewardThreshold = computed(() => (rewardPoints.value ?? 0) >= REWARD_THRESHOLD)

  const formattedRewardPoints = computed(() => {
    if (balancePoints.value === undefined) {
      return '-'
    }

    return formatNumberDisplayByLang(balancePoints.value)
  })

  const maxCurrencyValue = computed(() => {
    return company.value?.loyalty?.balance?.maxCurrencyValue
  })

  const conversionFormattedValue = computed(() => {
    return maxCurrencyValue.value?.formattedValue
  })

  const conversionCurrency = computed(() => {
    return maxCurrencyValue.value?.currency
  })

  const pointExpiry = computed(() => {
    return company.value?.loyalty?.balance?.pointExpiry
  })

  const expirationPoints = computed(() => {
    if (!pointExpiry.value?.length) return undefined

    const dateInstance = getDateInstance()

    return pointExpiry.value.find((pointExpiry) => {
      const date = dateInstance(pointExpiry.date)
      return isSameYearNow(getYear(date))
    })
  })

  return {
    company,
    invitationLink,
    formattedPostalAddress,
    postalAddress,
    balancePoints,
    rewardPoints,
    hasReachedRewardThreshold,
    formattedRewardPoints,
    maxCurrencyValue,
    conversionFormattedValue,
    conversionCurrency,
    pointExpiry,
    expirationPoints,
    setCompany,
    updateCompany
  }
})
